/**
 * Thread view — renders the message timeline.
 *
 * Three message kinds with distinct visual treatment (design handoff
 * primitives + screen-ticket.jsx):
 *   - client_email + staff_email: card with author + timestamp + body
 *   - internal_note: amber-tinted variant
 *   - system_event: muted single line
 *
 * Optimistic state:
 *   - sending: subtle opacity + animated dot
 *   - sent: normal
 *   - error: red border + retry button
 */

import { AlertTriangle, Clock, Mail, NotebookPen, Paperclip, Send } from 'lucide-react';

import type { Message, MessageAttachmentRef } from '@beacon/core';

import { Avatar } from '@/components/beacon/avatar.js';
import { Badge } from '@/components/ui/badge.js';
import type { CachedMessage } from '@/hooks/use-messages.js';
import { cn } from '@/lib/cn.js';

interface ThreadViewProps {
  messages: CachedMessage[];
  onRetry: (failed: CachedMessage) => void;
}

export function ThreadView({ messages, onRetry }: ThreadViewProps) {
  if (messages.length === 0) {
    return (
      <div className="text-center text-ink-300 text-[13px] py-8">
        No messages yet.
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {messages.map((m) => (
        <ThreadItem key={m.id} m={m} onRetry={onRetry} />
      ))}
    </div>
  );
}

function ThreadItem({
  m,
  onRetry,
}: {
  m: CachedMessage;
  onRetry: (m: CachedMessage) => void;
}) {
  if (m.type === 'system_event') {
    return (
      <div className="flex items-center gap-2 text-[11.5px] text-ink-300 mono pl-1">
        <span className="w-1 h-1 rounded-full bg-ink-400" />
        <span>
          {timeOf(m.createdAt)} · {m.bodyText}
        </span>
      </div>
    );
  }

  const isInbound = m.type === 'client_email';
  const isNote = m.type === 'internal_note';
  const sendStatus = m.__sendStatus;
  const authorName = friendlyName(m.authorUserId);

  return (
    <div className={cn('flex gap-3', isNote && 'pl-6')}>
      <Avatar name={authorName} size={30} />
      <div
        className={cn(
          // `transition` (not just `transition-colors`) so the opacity
          // shift on optimistic-send → reconciled also animates — spec
          // §3: message "appears immediately marked sending; status
          // flips to sent on response", which now visually fades from
          // 70% to 100% over 200ms instead of snapping.
          'flex-1 min-w-0 rounded-lg border overflow-hidden transition duration-200',
          isNote
            ? 'bg-warn-500/[0.06] border-warn-500/30'
            : 'bg-ink-800 border-ink-600',
          sendStatus === 'sending' && 'opacity-70',
          sendStatus === 'error' && '!border-danger-500/50 bg-danger-500/[0.05]'
        )}
      >
        <div
          className={cn(
            'px-3.5 py-2 flex items-center gap-2 text-[12px] border-b',
            isNote ? 'border-warn-500/20' : 'border-ink-600'
          )}
        >
          <span className="font-medium text-ink-100">{authorName}</span>
          {isInbound && (
            <Badge tone="blue">
              <Mail size={10} className="mr-0.5" />
              Inbound
            </Badge>
          )}
          {!isInbound && !isNote && (
            <Badge tone="slate">
              <Send size={10} className="mr-0.5" />
              Sent
            </Badge>
          )}
          {isNote && (
            <Badge tone="yellow">
              <NotebookPen size={10} className="mr-0.5" />
              Internal note
            </Badge>
          )}

          {sendStatus === 'sending' && (
            <span className="inline-flex items-center gap-1 mono text-[11px] text-beacon-300">
              <Clock size={10} />
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-beacon-400 animate-pulse" />
              sending…
            </span>
          )}
          {sendStatus === 'error' && (
            <button
              onClick={() => onRetry(m)}
              className="inline-flex items-center gap-1 mono text-[11px] text-danger-400 hover:text-danger-300 focus-ring rounded"
            >
              <AlertTriangle size={11} />
              {m.__errorMessage ?? 'send failed'} · retry
            </button>
          )}

          <span className="ml-auto text-ink-400 mono text-[11.5px]">
            {timeOf(m.createdAt)}
          </span>
        </div>
        <div className="px-3.5 py-3 text-[13px] text-ink-100 whitespace-pre-wrap leading-relaxed">
          {m.bodyText}
        </div>
        {m.attachments.length > 0 && (
          <AttachmentList attachments={m.attachments} />
        )}
      </div>
    </div>
  );
}

function AttachmentList({
  attachments,
}: {
  attachments: ReadonlyArray<MessageAttachmentRef>;
}) {
  return (
    <div className="px-3.5 pb-3 pt-0 flex flex-wrap gap-1.5">
      {attachments.map((a) => (
        <a
          key={a.id}
          href={`/api/attachments/${encodeURIComponent(a.id)}/download`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md bg-ink-900 border border-ink-600 px-2 py-1 text-[12px] text-ink-200 hover:text-ink-100 hover:border-ink-500 focus-ring transition-colors"
          title={`${a.mimeType} · ${formatBytes(a.sizeBytes)}`}
        >
          <Paperclip size={11} className="text-ink-300" />
          <span className="truncate max-w-[18rem]">{a.filename}</span>
          <span className="text-ink-400 mono text-[11px] shrink-0">
            {formatBytes(a.sizeBytes)}
          </span>
        </a>
      ))}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function timeOf(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function friendlyName(userId: string | null): string {
  if (!userId) return 'System';
  // TEMP — until /api/users is in place, derive from the seed convention.
  // Users in seed-dev.sql have id `user-<firstname>`.
  const first = userId.replace(/^user-/, '');
  return first.charAt(0).toUpperCase() + first.slice(1);
}
